/**
 * Color substitution utility for replacing color placeholders with actual colors
 * based on user's selected theme and color mode
 */

/**
 * Substitutes color placeholders in a value with actual colors
 * @param {any} value - The value to process (can be string, object, array, or primitive)
 * @param {string} userColorPalette - User's selected theme (e.g., 'summer', 'winter')
 * @param {string} userColorMode - User's selected color mode (e.g., 'light', 'dark') 
 * @param {Object} themePalettes - Theme palettes from metadata
 * @param {Object} fixedColors - Fixed colors from metadata
 * @param {string} currentKey - Current key being processed (for exclusion logic)
 * @returns {any} The value with color placeholders substituted
 */
export const substituteColors = (value, userColorPalette, userColorMode, themePalettes, fixedColors, currentKey = null) => {
  // Handle null/undefined values
  if (value == null) {
    return value;
  }

  // Handle string values - check for color placeholders
  if (typeof value === 'string') {
    // Skip color substitution for certain keys that should preserve palette keys
    if (currentKey === 'allowed_colors') {
      return value; // Keep palette keys as-is for allowed_colors
    }
    
    // Check if it's a palette color placeholder (e.g., "palette.1")
    if (value.startsWith('palette.') && userColorPalette && themePalettes[userColorPalette]) {
      const paletteColor = themePalettes[userColorPalette][value];
      if (paletteColor) {
        return paletteColor;
      }
    }
    
    // Check if it's a fixed color placeholder (e.g., "fixed.black")
    if (value.startsWith('fixed.') && fixedColors[value]) {
      return fixedColors[value];
    }
    
    // Return original value if no placeholder match
    return value;
  }

  // Handle arrays - recursively process each element
  if (Array.isArray(value)) {
    return value.map(item => 
      substituteColors(item, userColorPalette, userColorMode, themePalettes, fixedColors, currentKey)
    );
  }

  // Handle objects - recursively process each property
  if (typeof value === 'object') {
    const result = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = substituteColors(val, userColorPalette, userColorMode, themePalettes, fixedColors, key);
    }
    return result;
  }

  // Return primitive values as-is
  return value;
};

/**
 * Substitutes colors in grid data specifically
 * @param {Object} gridData - The grid data object from the backend
 * @param {string} userColorPalette - User's selected theme
 * @param {string} userColorMode - User's selected color mode
 * @param {Object} themePalettes - Theme palettes from metadata
 * @param {Object} fixedColors - Fixed colors from metadata
 * @returns {Object} Grid data with colors substituted
 */
export const substituteGridColors = (gridData, userColorPalette, userColorMode, themePalettes, fixedColors) => {
  if (!gridData) {
    return gridData;
  }

  return substituteColors(gridData, userColorPalette, userColorMode, themePalettes, fixedColors);
};

/**
 * Hook-like function to create a color substitution function with user theme data
 * @param {string} userColorPalette - User's selected theme
 * @param {string} userColorMode - User's selected color mode  
 * @param {Object} themePalettes - Theme palettes from metadata
 * @param {Object} fixedColors - Fixed colors from metadata
 * @returns {Function} A function that can substitute colors in any value
 */
export const createColorSubstituter = (userColorPalette, userColorMode, themePalettes, fixedColors) => {
  return (value) => substituteColors(value, userColorPalette, userColorMode, themePalettes, fixedColors);
};